import { useState, useRef, useEffect } from 'react';

import PropTypes from 'prop-types';
import { BsCheckCircle, BsEnvelope } from 'react-icons/bs';

import Alert from '#globalComponents/Alert/Alert';
import Button from '#globalComponents/Button/Button';
import TextInput from '#globalComponents/Forms/TextInput';
import Modal from '#globalComponents/Overlay/Modal';
import Panel from '#globalComponents/Overlay/Panel';

import { useEmailCampaignsContext } from '../../../contexts/EmailCampaignsContext';

const CreateCampaignModal = ({ isOpen, onClose }) => {
	const [campaignName, setCampaignName] = useState('');
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [errors, setErrors] = useState({});
	const [showSuccess, setShowSuccess] = useState(false);

	const inputRef = useRef(null);
	const { handleCreateCampaign } = useEmailCampaignsContext();

	// Focus input when modal opens
	useEffect(() => {
		if (isOpen && inputRef.current) {
			setTimeout(() => {
				inputRef.current?.focus();
			}, 100);
		}
	}, [isOpen]);

	// Reset form when modal opens/closes
	useEffect(() => {
		if (isOpen) {
			setCampaignName('');
			setErrors({});
			setShowSuccess(false);
		}
	}, [isOpen]);

	const validateForm = () => {
		const newErrors = {};

		if (!campaignName.trim()) {
			newErrors.campaignName = 'Kampagnen-Name ist erforderlich';
		} else if (campaignName.trim().length < 2) {
			newErrors.campaignName = 'Kampagnen-Name muss mindestens 2 Zeichen haben';
		} else if (campaignName.trim().length > 100) {
			newErrors.campaignName = 'Kampagnen-Name darf höchstens 100 Zeichen haben';
		}

		setErrors(newErrors);
		return Object.keys(newErrors).length === 0;
	};

	const handleSubmit = async (e) => {
		e.preventDefault();

		if (!validateForm()) {
			return;
		}

		setIsSubmitting(true);

		try {
			// AI-generated campaigns are no longer created; existing ones keep running. Personalisation
			// comes from custom columns used as template variables.
			await handleCreateCampaign({
				name: campaignName.trim(),
				campaignType: 'template',
			});

			setShowSuccess(true);

			setTimeout(() => {
				onClose();
				setShowSuccess(false);
			}, 1500);
		} catch (error) {
			setErrors({
				submit: error.message || 'Fehler beim Erstellen der Kampagne. Bitte versuchen Sie es erneut.',
			});
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleKeyDown = (e) => {
		if (e.key === 'Enter' && !e.shiftKey) {
			e.preventDefault();
			handleSubmit(e);
		}
	};

	return (
		<Modal isOpen={isOpen} onClose={onClose} size="s" as="form" onSubmit={handleSubmit}>
			{showSuccess && (
				<Panel.Body>
					<div className="text-center py-8">
						<div className="w-16 h-16 bg-success-200 rounded-full flex items-center justify-center mx-auto mb-4">
							<BsCheckCircle className="w-10 h-10 text-success-700" />
						</div>
						<h3 className="text-lg font-semibold text-text mb-2">Kampagne erstellt!</h3>
						<p className="text-text">Ihre neue E-Mail-Kampagne wurde erfolgreich angelegt.</p>
					</div>
				</Panel.Body>
			)}
			{!showSuccess && (
				<Panel.Header
					icon={BsEnvelope}
					onClose={onClose}
					subtitle="Erstellen Sie eine neue automatisierte E-Mail-Kampagne für Ihre Leads."
				>
					Neue E-Mail-Kampagne erstellen
				</Panel.Header>
			)}
			{!showSuccess && (
				<Panel.Body>
					{errors.submit && (
						<Alert tone="warning" className="mb-4">
							{errors.submit}
						</Alert>
					)}

					<div>
						<label className="block text-sm font-medium text-text mb-2">Kampagnen-Name *</label>
						<TextInput
							ref={inputRef}
							value={campaignName}
							onChange={(e) => setCampaignName(e.target.value)}
							onKeyDown={handleKeyDown}
							error={Boolean(errors.campaignName)}
							placeholder="z.B. Willkommens-Serie, Newsletter, Produkt-Launch..."
							maxLength={100}
						/>
						{errors.campaignName && <p className="mt-1 text-xs text-warning-700">{errors.campaignName}</p>}
						<p className="mt-1 text-xs text-bgBase-800">{campaignName.length}/100 Zeichen</p>
					</div>
				</Panel.Body>
			)}
			{!showSuccess && (
				<Panel.Footer className="justify-end">
					<Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
						Abbrechen
					</Button>
					<Button
						type="submit"
						variant="solid"
						disabled={isSubmitting || !campaignName.trim()}
						className="min-w-[120px]"
					>
						{isSubmitting ? (
							<div className="flex items-center gap-2">
								<div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
								<span>Erstellen...</span>
							</div>
						) : (
							'Kampagne erstellen'
						)}
					</Button>
				</Panel.Footer>
			)}
		</Modal>
	);
};

CreateCampaignModal.propTypes = {
	isOpen: PropTypes.bool.isRequired,
	onClose: PropTypes.func.isRequired,
};

export default CreateCampaignModal;
